/**
 * Tracks whether a content-only transition hold (#1352) owns `registrationTransitionHoldActive`.
 * Early queue/assets flush exits and build failures must release only when this pin took the hold —
 * never clear an in-flight registration resync hold.
 */
export type ContentTransitionHoldOwnership = {
  owned: boolean
  /** Monotonic clock reading when this pin took the hold; unset while not owned. */
  sinceMs?: number
}

/**
 * Upper bound for a content-only pin. It exists to cover a single asset-index rebuild, so anything
 * past this is a latch (#1359): the hold freezes `surfaceForRender` *and* suppresses every stall
 * detector, so it can never report itself and only a restart clears it.
 */
export const CONTENT_TRANSITION_HOLD_MAX_MS = 10_000

export function takeContentTransitionHoldOwnership(
  state: ContentTransitionHoldOwnership,
  holdAlreadyActive: boolean,
  nowMs: number = performance.now()
): boolean {
  if (holdAlreadyActive || state.owned) {
    return false
  }
  state.owned = true
  state.sinceMs = nowMs
  return true
}

export function releaseOwnedContentTransitionHold(
  state: ContentTransitionHoldOwnership
): boolean {
  if (!state.owned) {
    return false
  }
  state.owned = false
  state.sinceMs = undefined
  return true
}

export function clearContentTransitionHoldOwnership(
  state: ContentTransitionHoldOwnership
): void {
  state.owned = false
  state.sinceMs = undefined
}

/** True when an owned pin has outlived {@link CONTENT_TRANSITION_HOLD_MAX_MS} and must be force released. */
export function isContentTransitionHoldOverdue(
  state: ContentTransitionHoldOwnership,
  nowMs: number,
  maxMs: number = CONTENT_TRANSITION_HOLD_MAX_MS
): boolean {
  if (!state.owned || state.sinceMs == null) {
    return false
  }
  return nowMs - state.sinceMs >= maxMs
}
